"use client";

import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { Button } from "@/components/shadcn/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/shadcn/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { toast } from "sonner";
import { X } from "lucide-react";

interface PrivacySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
}

interface PrivacySettings {
  contact_country_visible: string;
  contact_address_visible: string;
  contact_city_visible: string;
  contact_region_visible: string;
  contact_postal_visible: string;
  contact_phone_visible: string;
  contact_skype_visible: string;
  contact_fax_visible: string;
  contact_email_visible: string;
  contact_url_visible: string;
  agency_name_visible: string;
}

export default function PrivacySettingsModal({
  isOpen,
  onClose,
  entityId,
}: PrivacySettingsModalProps) {
  const { data: session } = useSession();
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{
    field: keyof PrivacySettings;
    value: string;
  } | null>(null);

  // Check if user has permission to edit this profile
  const canEdit =
    session?.user?.is_admin || session?.user?.entity_id === entityId || session?.user?.is_pseudo;

  useEffect(() => {
    if (isOpen && entityId && canEdit) {
      fetchPrivacySettings();
    }
  }, [isOpen, entityId, canEdit]);

  const fetchPrivacySettings = async () => {
    if (!entityId) return;

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`/next/api/user/privacy-check?entityId=${entityId}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to fetch privacy settings");
        return;
      }

      setPrivacySettings(data.privacySettings);
    } catch (err) {
      console.error("Privacy settings fetch error:", err);
      setError("Failed to fetch privacy settings");
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyChange = (field: keyof PrivacySettings, value: string) => {
    setPendingUpdate({ field, value });
    setShowConfirmation(true);
  };

  const confirmUpdate = async () => {
    if (!pendingUpdate || !entityId || !session?.user) return;

    setSaving(true);
    setShowConfirmation(false);

    try {
      const response = await fetch(`/next/api/user/preferences?entityId=${entityId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          category: "privacy",
          prefName: pendingUpdate.field,
          value: pendingUpdate.value,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update privacy setting");
      }

      // Update local state
      setPrivacySettings((prev) =>
        prev ? { ...prev, [pendingUpdate.field]: pendingUpdate.value } : null
      );
      toast.success(`${getFieldLabel(pendingUpdate.field)} privacy setting updated successfully`);
    } catch (err) {
      console.error("Privacy setting update error:", err);
      toast.error(
        `Failed to update ${getFieldLabel(pendingUpdate.field)} privacy setting: ${err instanceof Error ? err.message : "Unknown error"}`
      );
    } finally {
      setSaving(false);
      setPendingUpdate(null);
    }
  };

  const cancelUpdate = () => {
    setShowConfirmation(false);
    setPendingUpdate(null);
  };

  const getPrivacyOptions = () => [
    { value: "y", label: "Everyone" },
    { value: "n", label: "No one" },
    { value: "outsourcer", label: "Outsourcers only" },
  ];

  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      contact_country_visible: "Country",
      contact_address_visible: "Address",
      contact_city_visible: "City",
      contact_region_visible: "Region/State",
      contact_postal_visible: "Postal Code",
      contact_phone_visible: "Phone",
      contact_skype_visible: "Skype",
      contact_fax_visible: "Fax",
      contact_email_visible: "Email",
      contact_url_visible: "Website",
      agency_name_visible: "Agency Name",
    };
    return labels[field] || field;
  };

  const getFieldDescription = (field: string) => {
    const descriptions: Record<string, string> = {
      contact_country_visible: "Who can see your country location",
      contact_address_visible: "Who can see your full address",
      contact_city_visible: "Who can see your city",
      contact_region_visible: "Who can see your region or state",
      contact_postal_visible: "Who can see your postal code",
      contact_phone_visible: "Who can see your phone number",
      contact_skype_visible: "Who can see your Skype username",
      contact_fax_visible: "Who can see your fax number",
      contact_email_visible: "Who can see your email address",
      contact_url_visible: "Who can see your website URL",
      agency_name_visible: "Who can see your agency name",
    };
    return descriptions[field] || "";
  };

  if (!canEdit) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Access Denied</DialogTitle>
            <DialogDescription>
              You don't have permission to edit privacy settings for this profile.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4">
            <p>
              Debug info: entityId={entityId}, session.entity_id={session?.user?.entity_id},
              is_admin={session?.user?.is_admin}, is_pseudo={session?.user?.is_pseudo}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      {/* Custom Modal Implementation */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Semi-transparent overlay */}
          <div className="fixed inset-0 bg-black/20" onClick={onClose}></div>

          {/* Dialog content */}
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Privacy Settings</h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Manage who can see your profile information. You can control visibility for each
                field individually.
              </p>

              <div className="space-y-6">
                {loading ? (
                  <div className="text-center py-8">Loading privacy settings...</div>
                ) : error ? (
                  <div className="text-red-600 p-4 bg-red-50 rounded-lg">{error}</div>
                ) : privacySettings ? (
                  <div className="space-y-4">
                    {Object.entries(privacySettings).map(([field, value]) => (
                      <div key={field} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex flex-col space-y-2">
                          <label className="font-medium">{getFieldLabel(field)}</label>
                          <p className="text-sm text-gray-600">{getFieldDescription(field)}</p>
                          <Select
                            value={value}
                            onValueChange={(newValue) =>
                              handlePrivacyChange(field as keyof PrivacySettings, newValue)
                            }
                            disabled={saving}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getPrivacyOptions().map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">No privacy settings found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Original Dialog component (commented out for testing) */}
      {/* <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto relative z-[9999]">
          <DialogHeader>
            <DialogTitle>Privacy Settings Test</DialogTitle>
            <DialogDescription>
              This is a test dialog to see if the Dialog component works.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-6 space-y-4">
            <div className="p-4 bg-blue-100 border border-blue-300 rounded">
              <p className="text-blue-800 font-bold">SUCCESS: Dialog is working!</p>
              <p>isOpen: {isOpen.toString()}</p>
              <p>entityId: {entityId}</p>
              <p>canEdit: {canEdit.toString()}</p>
            </div>
            
            <Button onClick={onClose} className="w-full">
              Close Dialog
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          {/* Semi-transparent overlay */}
          <div className="fixed inset-0 bg-black/20" onClick={cancelUpdate}></div>

          {/* Dialog content */}
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Confirm Privacy Setting Update</h2>
                <button onClick={cancelUpdate} className="text-gray-500 hover:text-gray-700">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Please confirm that you want to update this privacy setting. This change will affect
                who can see this information on your profile.
              </p>

              {pendingUpdate && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded mb-4">
                  <p>
                    Are you sure you want to update the{" "}
                    <strong>{getFieldLabel(pendingUpdate.field)}</strong> privacy setting to{" "}
                    <strong>
                      "{getPrivacyOptions().find((opt) => opt.value === pendingUpdate.value)?.label}
                      "
                    </strong>
                    ?
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={cancelUpdate} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={confirmUpdate} disabled={saving}>
                  {saving ? "Updating..." : "Confirm"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
